import { useCallback, useEffect, useRef, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { useCellOverlayContext } from './cellOverlayContext'

const HOLD_MS = 700
const RING_RADIUS = 11
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

interface HoldToDeleteButtonProps {
  onConfirm: () => void
  disabled?: boolean
  className?: string
}

export const HoldToDeleteButton = ({ onConfirm, disabled, className }: HoldToDeleteButtonProps) => {
  const { t } = useTar()
  const { notifyOpenChange } = useCellOverlayContext()
  const [progress, setProgress] = useState(0) // 0..1
  const startRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)
  const completedRef = useRef(false)

  const cancel = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    startRef.current = null
    completedRef.current = false
    setProgress(0)
  }, [])

  const tick = useCallback((now: number) => {
    if (startRef.current === null) return
    const elapsed = now - startRef.current
    const p = Math.min(1, elapsed / HOLD_MS)
    setProgress(p)
    if (p >= 1) {
      if (!completedRef.current) {
        completedRef.current = true
        onConfirm()
      }
      cancel()
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [onConfirm, cancel])

  const begin = useCallback(() => {
    if (disabled) return
    startRef.current = performance.now()
    rafRef.current = requestAnimationFrame(tick)
  }, [disabled, tick])

  useEffect(() => () => cancel(), [cancel])

  // Stroke goes from full (no fill) to zero (full ring)
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress)

  return (
    <Tooltip onOpenChange={notifyOpenChange}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t('common.notebookCellHoldToDelete')}
          disabled={disabled}
          className={cn(
            'relative inline-flex h-6 w-7 items-center justify-center rounded text-red-500 transition-colors',
            'hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed',
            className,
          )}
          onPointerDown={begin}
          onPointerUp={cancel}
          onPointerLeave={cancel}
          onPointerCancel={cancel}
        >
          <Trash2 className="h-3.5 w-3.5" />
          <svg
            className="pointer-events-none absolute inset-0 m-auto h-6 w-6 -rotate-90"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <circle
              cx="12"
              cy="12"
              r={RING_RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              style={{ transition: progress === 0 ? 'stroke-dashoffset .15s ease-out' : 'none' }}
            />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent>{t('common.notebookCellHoldToDelete')}</TooltipContent>
    </Tooltip>
  )
}

export default HoldToDeleteButton
